// Direction B's list-page filters (ManifestTablePage in src/prototype/DirectionB.tsx): a collapsible rail
// left of the table, a toggle button in the toolbar, chips for the active filters. All presentational:
// the page owns every value, the open state and any debounce.
//
//   const [railOpen, setRailOpen] = useState(true);
//   <ListLayout rail={railOpen && (
//     <FilterRail id="email-filters" onReset={resetFilters}>
//       <FilterSection legend={t('…status')}>
//         <FilterOption control={<Checkbox checked={…} onCheckedChange={…} />} label="Draft" count={12} />
//       </FilterSection>
//     </FilterRail>
//   )}>
//     <DataTable toolbar={<>
//       <DataTableToolbar search={…}><FilterToggle open={railOpen} onOpenChange={setRailOpen} controls="email-filters" /></DataTableToolbar>
//       <FilterChips chips={…} onClearAll={resetFilters} />
//     </>} … />
//   </ListLayout>
import { ListFilterIcon, XIcon } from 'lucide-react';
import type * as React from 'react';

import { Badge } from '@/components/lumen/badge';
import { Button } from '@/components/lumen/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatTemplate } from '@/lib/format-template';
import { cn } from '@/lib/utils';

/**
 * The page body of a list page: the rail on the left from 1280px up (it stacks above the table below
 * that, where B has no room for two panes), the table on the right. Pass `rail={false}` to close it.
 */
export function ListLayout({
  rail,
  children,
  className,
}: {
  rail?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const hasRail = Boolean(rail);
  return (
    <div
      data-slot="list-layout"
      data-rail={hasRail || undefined}
      className={cn('grid grid-cols-1 items-start gap-5', hasRail && 'xl:grid-cols-[14.5rem_minmax(0,1fr)]', className)}
    >
      {hasRail && rail}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export interface FilterRailProps {
  /** Ties the rail to its FilterToggle (`controls`). */
  id?: string;
  /** Default "Filter". */
  title?: React.ReactNode;
  /** Shows the "Reset" link. */
  onReset?: () => void;
  children: React.ReactNode;
  className?: string;
}

export function FilterRail({ id, title, onReset, children, className }: FilterRailProps) {
  const { t } = useLanguage();
  const heading = title ?? t('lumen.table.filter');
  return (
    <aside
      id={id}
      data-slot="filter-rail"
      aria-label={typeof heading === 'string' ? heading : t('lumen.table.filter')}
      // Sticky under the 48px top bar while the table scrolls the page.
      className={cn('grid min-w-0 content-start gap-4 xl:sticky xl:top-16', className)}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-bold text-fg text-sm">{heading}</h2>
        {onReset && (
          <Button type="button" variant="link" size="xs" className="h-auto px-0" onClick={onReset}>
            {t('lumen.table.resetFilters')}
          </Button>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-1">{children}</div>
    </aside>
  );
}

/** One group of the rail: a legend over its options or its one control. */
export function FilterSection({
  legend,
  children,
  className,
}: {
  legend: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <fieldset
      data-slot="filter-section"
      className={cn('grid min-w-0 content-start gap-0.5 border-line border-t pt-3.5', className)}
    >
      <legend className="float-left mb-2 w-full font-semibold text-fg-muted text-xs">{legend}</legend>
      {children}
    </fieldset>
  );
}

/**
 * A checkbox or radio row: the control (a Lumen Checkbox or RadioGroupItem), its label, and an optional
 * count or code on the right in mono. The whole row is the label, so it toggles the control.
 */
export function FilterOption({
  control,
  label,
  count,
  className,
}: {
  control: React.ReactNode;
  label: React.ReactNode;
  count?: React.ReactNode;
  className?: string;
}) {
  return (
    <label
      data-slot="filter-option"
      className={cn(
        '-mx-1.5 flex cursor-pointer items-center gap-2.5 rounded-tag px-1.5 py-1 text-13 text-fg hover:bg-tint',
        className
      )}
    >
      {control}
      <span className="min-w-0 flex-1">{label}</span>
      {count !== undefined && count !== null && (
        <span className="font-mono text-[0.6875rem] text-fg-muted">{count}</span>
      )}
    </label>
  );
}

/** The toolbar button that opens and closes the rail; shows how many filters are on. */
export function FilterToggle({
  open,
  onOpenChange,
  activeCount = 0,
  controls,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeCount?: number;
  /** The rail's id. */
  controls?: string;
  className?: string;
}) {
  const { t } = useLanguage();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-pressed={open}
      aria-controls={controls}
      onClick={() => onOpenChange(!open)}
      className={className}
    >
      <ListFilterIcon aria-hidden />
      {open ? t('lumen.table.hideFilter') : t('lumen.table.filter')}
      {activeCount > 0 && (
        <Badge variant="info" className="font-mono">
          {activeCount}
        </Badge>
      )}
    </Button>
  );
}

export interface FilterChip {
  key: string;
  /** What the chip shows, e.g. "Status: Draft". */
  label: React.ReactNode;
  /** Plain text for the remove button's name when `label` is not a string. */
  name?: string;
  /** Shows the remove button. */
  onRemove?: () => void;
}

/** The active filters as removable chips, under the toolbar. Renders nothing when there are none. */
export function FilterChips({
  chips,
  onClearAll,
  className,
}: {
  chips: FilterChip[];
  onClearAll?: () => void;
  className?: string;
}) {
  const { t } = useLanguage();
  if (chips.length === 0) return null;
  return (
    <div
      data-slot="filter-chips"
      className={cn('flex flex-wrap items-center gap-2 border-line border-b px-3 py-2 sm:px-4', className)}
    >
      <ul aria-label={t('lumen.table.activeFilters')} className="flex flex-wrap items-center gap-1.5">
        {chips.map((chip) => (
          <li
            key={chip.key}
            className="inline-flex h-6 items-center gap-0.5 rounded-tag border border-label/25 bg-label/10 pl-2 font-semibold text-info-ink text-xs"
          >
            <span>{chip.label}</span>
            {chip.onRemove ? (
              <button
                type="button"
                onClick={chip.onRemove}
                aria-label={formatTemplate(t('lumen.table.removeFilter'), {
                  label: chip.name ?? (typeof chip.label === 'string' ? chip.label : chip.key),
                })}
                className="inline-flex size-5 items-center justify-center rounded-tag outline-none transition-colors hover:bg-label/15 focus-visible:ring-2 focus-visible:ring-label/40"
              >
                <XIcon aria-hidden className="size-3" />
              </button>
            ) : (
              <span className="w-1.5" />
            )}
          </li>
        ))}
      </ul>
      {onClearAll && (
        <Button type="button" variant="link" size="xs" className="h-auto px-0" onClick={onClearAll}>
          {t('lumen.table.clearFilters')}
        </Button>
      )}
    </div>
  );
}
